import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  conditionsEditable,
  formatMoney,
  installmentAmounts,
  OFFER_LOG_KINDS,
  OFFER_LOG_LABELS,
  type OfferLogEntry,
  paymentText,
  type OfferLogKind,
  type QuoteLookups,
  type QuoteResponse,
} from '@k-management/shared';
import { useState } from 'react';
import { useCan } from '../../auth/AuthProvider.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatTimestamp } from '../../lib/format.js';
import { quoteKeys } from './quotes-ui.js';

/**
 * «Integrazioni» of a quote (decision of 05/10/2026): what the negotiation
 * adds. The references in K-Rev, the payment plan and the notes for the client
 * go into the documents and change until the offer is sent; the log stays
 * inside K-Rev.
 */
export function IntegrationsView({
  quote,
  lookups,
  onChanged,
}: {
  quote: QuoteResponse;
  lookups: QuoteLookups;
  onChanged: (updated?: QuoteResponse) => Promise<void>;
}) {
  const canWrite = useCan('preventivatore.write');
  const editable = canWrite && conditionsEditable(quote.state);
  return (
    <section className="stack">
      {!conditionsEditable(quote.state) && (
        <p className="notice info">
          L’offerta è già stata inviata: riferimenti, piano di pagamento e note per il cliente si
          cambiano con una nuova versione. Il diario si aggiorna sempre.
        </p>
      )}
      <Contacts quote={quote} lookups={lookups} editable={editable} onChanged={onChanged} />
      <PlanEditor quote={quote} lookups={lookups} editable={editable} onChanged={onChanged} />
      <ClientNotes quote={quote} editable={editable} onChanged={onChanged} />
      {quote.number && <OfferLog quote={quote} />}
    </section>
  );
}

/**
 * «I vostri riferimenti in K-Rev» on the offer (2.58): the project manager,
 * the commerciale and the administration. Changing them keeps the validation.
 */
function Contacts({
  quote,
  lookups,
  editable,
  onChanged,
}: {
  quote: QuoteResponse;
  lookups: QuoteLookups;
  editable: boolean;
  onChanged: (updated?: QuoteResponse) => Promise<void>;
}) {
  const save = useMutation({
    mutationFn: (body: { projectManagerId?: string | null; adminContactId?: string | null }) =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}`, { method: 'PATCH', body }),
    onSuccess: (updated) => onChanged(updated),
  });
  const picker = (
    label: string,
    field: 'projectManagerId' | 'adminContactId',
    person: QuoteResponse['projectManager'],
  ) => (
    <Field label={label}>
      {editable ? (
        <select
          value={person?.id ?? ''}
          disabled={save.isPending}
          onChange={(event) => save.mutate({ [field]: event.target.value || null })}
        >
          <option value="">— nessuno —</option>
          {lookups.colleagues.map((colleague) => (
            <option key={colleague.id} value={colleague.id}>
              {colleague.name}
            </option>
          ))}
          {person && !lookups.colleagues.some((colleague) => colleague.id === person.id) && (
            <option value={person.id}>{person.name}</option>
          )}
        </select>
      ) : (
        <span>{person ? `${person.name} · ${person.email}` : '—'}</span>
      )}
    </Field>
  );
  return (
    <section className="card stack">
      <h2>Riferimenti in K-Rev</h2>
      <p className="muted small">
        Vanno nell’Offerta economica, sotto le condizioni, con nome ed e-mail. L’amministrazione è
        proposta da Tabelle › Parametri (RIF-AMMINISTRAZIONE). Cambiarli non chiede una nuova
        convalida del CFO.
      </p>
      <div className="form-grid">
        {picker('Project management', 'projectManagerId', quote.projectManager)}
        <Field label="Commerciale">
          <span>
            {quote.owner ? `${quote.owner.name} · ${quote.owner.email}` : '—'}{' '}
            <span className="muted small">(il commerciale di riferimento)</span>
          </span>
        </Field>
        {picker('Amministrazione', 'adminContactId', quote.adminContact)}
      </div>
      {save.error && <p className="error">{save.error.message}</p>}
    </section>
  );
}

interface PlanRow {
  description: string;
  percent: string;
  amount: string;
  termsCode: string;
  notes: string;
}

function rowsOf(quote: QuoteResponse): PlanRow[] {
  return quote.installments.map((item) => ({
    description: item.description,
    percent: item.percent === null ? '' : String(Number(item.percent)).replace('.', ','),
    amount: item.amount === null ? '' : item.amount.replace('.', ','),
    termsCode: item.termsCode ?? '',
    notes: item.notes ?? '',
  }));
}

/** The payment plan by instalments: one per row, a share or an amount each. */
function PlanEditor({
  quote,
  lookups,
  editable,
  onChanged,
}: {
  quote: QuoteResponse;
  lookups: QuoteLookups;
  editable: boolean;
  onChanged: (updated?: QuoteResponse) => Promise<void>;
}) {
  const [rows, setRows] = useState<PlanRow[]>(() => rowsOf(quote));
  const [error, setError] = useState('');
  const net = Number(quote.totals.net);
  const sum = installmentAmounts(
    rows.map((row) => ({
      percent: row.percent ? row.percent.replace(',', '.') : null,
      amount: row.amount ? row.amount.replace(/\./g, '').replace(',', '.') : null,
    })),
    net,
  );
  const changed = JSON.stringify(rows) !== JSON.stringify(rowsOf(quote));
  const save = useMutation({
    mutationFn: () =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}/installments`, {
        method: 'PUT',
        body: {
          installments: rows.map((row) => ({
            description: row.description,
            percent: row.percent || null,
            amount: row.amount || null,
            termsCode: row.termsCode || null,
            notes: row.notes || null,
          })),
        },
      }),
    onSuccess: async (updated) => {
      setError('');
      setRows(rowsOf(updated));
      await onChanged(updated);
    },
    onError: (failure: Error) => setError(failure.message),
  });
  const set = (index: number, change: Partial<PlanRow>) =>
    setRows((current) => current.map((row, at) => (at === index ? { ...row, ...change } : row)));
  const termsLabel = (code: string | null) =>
    code ? (lookups.paymentTerms.find((term) => term.code === code)?.description ?? code) : null;

  return (
    <section className="card stack">
      <div className="row between">
        <h2>Piano di pagamento</h2>
        {rows.length > 0 &&
          (sum.matches ? (
            <span className="pill ok">uguale al totale</span>
          ) : (
            <span className="pill warn">
              rate {formatMoney(sum.total)} su {formatMoney(net)}
            </span>
          ))}
      </div>
      <p className="muted small">
        Le rate, una per riga: quando si fattura, quanto (in percentuale o come importo) e quando si
        incassa. Va nell’Offerta economica e nel documento da modello. Un termine diverso dallo
        standard chiede la convalida del CFO.
      </p>
      {paymentText(quote, lookups) && (
        <p className="small">
          <span className="muted">Sull’offerta: </span>
          <span className="pre-line">{paymentText(quote, lookups)}</span>
        </p>
      )}
      {rows.length === 0 && !editable && <p className="muted">Nessun piano a rate.</p>}
      {rows.length > 0 && (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Quando si fattura</th>
                <th className="right">%</th>
                <th className="right">Importo</th>
                <th>Incasso</th>
                <th>Note</th>
                {editable && <th aria-label="Azioni" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) =>
                editable ? (
                  <tr key={index}>
                    <td>
                      <input
                        aria-label="Quando si fattura"
                        value={row.description}
                        maxLength={200}
                        placeholder="Alla firma"
                        onChange={(event) => set(index, { description: event.target.value })}
                      />
                    </td>
                    <td className="right">
                      <input
                        aria-label="Percentuale"
                        inputMode="decimal"
                        className="narrow"
                        value={row.percent}
                        onChange={(event) =>
                          set(index, { percent: event.target.value, amount: '' })
                        }
                      />
                    </td>
                    <td className="right">
                      <input
                        aria-label="Importo"
                        inputMode="decimal"
                        className="narrow"
                        value={row.amount}
                        placeholder={sum.amounts[index] ? formatMoney(sum.amounts[index]!) : ''}
                        onChange={(event) =>
                          set(index, { amount: event.target.value, percent: '' })
                        }
                      />
                    </td>
                    <td>
                      <select
                        aria-label="Incasso"
                        value={row.termsCode}
                        onChange={(event) => set(index, { termsCode: event.target.value })}
                      >
                        <option value="">Come l’offerta</option>
                        {lookups.paymentTerms.map((term) => (
                          <option key={term.code} value={term.code}>
                            {term.description}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        aria-label="Note"
                        value={row.notes}
                        maxLength={300}
                        onChange={(event) => set(index, { notes: event.target.value })}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="small danger"
                        aria-label="Togli la rata"
                        onClick={() =>
                          setRows((current) => current.filter((_, at) => at !== index))
                        }
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ) : (
                  <tr key={index}>
                    <td>{row.description}</td>
                    <td className="right">{row.percent ? `${row.percent}%` : '—'}</td>
                    <td className="right">{formatMoney(sum.amounts[index] ?? 0)}</td>
                    <td>{termsLabel(row.termsCode || null) ?? 'Come l’offerta'}</td>
                    <td>{row.notes || '—'}</td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      )}
      {editable && (
        <div className="row">
          <button
            type="button"
            onClick={() =>
              setRows((current) => [
                ...current,
                { description: '', percent: '', amount: '', termsCode: '', notes: '' },
              ])
            }
          >
            + Aggiungi rata
          </button>
          <button
            type="button"
            className="primary"
            disabled={!changed || save.isPending}
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Salvataggio…' : 'Salva il piano'}
          </button>
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </section>
  );
}

/** Notes for the client: printed under the lines of the offer (2.58). */
function ClientNotes({
  quote,
  editable,
  onChanged,
}: {
  quote: QuoteResponse;
  editable: boolean;
  onChanged: (updated?: QuoteResponse) => Promise<void>;
}) {
  const [text, setText] = useState(quote.clientNotes ?? '');
  const [error, setError] = useState('');
  const save = useMutation({
    mutationFn: () =>
      apiRequest<QuoteResponse>(`/quotes/${quote.id}`, {
        method: 'PATCH',
        body: { clientNotes: text || null },
      }),
    onSuccess: async (updated) => {
      setError('');
      await onChanged(updated);
    },
    onError: (failure: Error) => setError(failure.message),
  });
  return (
    <section className="card stack">
      <h2>Note per il cliente</h2>
      <p className="muted small">
        Precisazioni concordate, riferimenti a e-mail, accordi particolari: compaiono nell’Offerta
        economica sotto le righe, prima del totale, e nel documento da modello.
      </p>
      {editable ? (
        <textarea
          rows={4}
          maxLength={2000}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onBlur={() => {
            if (text !== (quote.clientNotes ?? '')) save.mutate();
          }}
        />
      ) : (
        <p className="pre-line">{quote.clientNotes || '—'}</p>
      )}
      {error && <p className="error">{error}</p>}
    </section>
  );
}

/** «2026-10-06T09:20» for a datetime-local field, in the time of the browser. */
function localInput(value: Date): string {
  const shifted = new Date(value.getTime() - value.getTimezoneOffset() * 60_000);
  return shifted.toISOString().slice(0, 16);
}

/** The log of the negotiation, one per offer and shared by its versions; internal. */
function OfferLog({ quote }: { quote: QuoteResponse }) {
  const queryClient = useQueryClient();
  const canWrite = useCan('preventivatore.write');
  const key = [...quoteKeys.quote(quote.id), 'log'];
  const log = useQuery({
    queryKey: key,
    queryFn: () => apiRequest<OfferLogEntry[]>(`/quotes/${quote.id}/log`),
  });
  const empty = {
    kind: 'telefonata' as OfferLogKind,
    contact: '',
    text: '',
    at: localInput(new Date()),
  };
  const [draft, setDraft] = useState(empty);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState('');
  const store = (entries: OfferLogEntry[]) => {
    queryClient.setQueryData(key, entries);
    setDraft({ ...empty, at: localInput(new Date()) });
    setEditing(null);
    setError('');
  };
  const body = () => ({
    occurredAt: new Date(draft.at).toISOString(),
    kind: draft.kind,
    contact: draft.contact || null,
    text: draft.text,
  });
  const save = useMutation({
    mutationFn: () =>
      editing
        ? apiRequest<OfferLogEntry[]>(`/quotes/${quote.id}/log/${editing}`, {
            method: 'PATCH',
            body: body(),
          })
        : apiRequest<OfferLogEntry[]>(`/quotes/${quote.id}/log`, { method: 'POST', body: body() }),
    onSuccess: store,
    onError: (failure: Error) => setError(failure.message),
  });
  const remove = useMutation({
    mutationFn: (entryId: string) =>
      apiRequest<OfferLogEntry[]>(`/quotes/${quote.id}/log/${entryId}`, { method: 'DELETE' }),
    onSuccess: store,
    onError: (failure: Error) => setError(failure.message),
  });

  return (
    <section className="card stack">
      <h2>Diario della trattativa</h2>
      <p className="muted small">
        Telefonate, e-mail, incontri, richieste del cliente. Solo per K-Rev: non va nei documenti. È
        lo stesso per tutte le versioni dell’offerta; una nota si corregge solo da chi l’ha scritta,
        entro 24 ore.
      </p>
      {canWrite && (
        <div className="stack tight">
          <div className="row">
            <Field label="Tipo">
              <select
                value={draft.kind}
                onChange={(event) =>
                  setDraft({ ...draft, kind: event.target.value as OfferLogKind })
                }
              >
                {OFFER_LOG_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {OFFER_LOG_LABELS[kind]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Con chi">
              <input
                value={draft.contact}
                maxLength={200}
                placeholder="Ufficio acquisti"
                onChange={(event) => setDraft({ ...draft, contact: event.target.value })}
              />
            </Field>
            <Field label="Quando">
              <input
                type="datetime-local"
                value={draft.at}
                onChange={(event) => setDraft({ ...draft, at: event.target.value })}
              />
            </Field>
          </div>
          <Field label="Cosa">
            <textarea
              rows={3}
              maxLength={4000}
              value={draft.text}
              onChange={(event) => setDraft({ ...draft, text: event.target.value })}
            />
          </Field>
          <div className="row">
            <button
              type="button"
              className="primary"
              disabled={!draft.text.trim() || save.isPending}
              onClick={() => save.mutate()}
            >
              {editing ? 'Salva la correzione' : 'Annota'}
            </button>
            {editing && (
              <button type="button" onClick={() => store(log.data ?? [])}>
                Annulla
              </button>
            )}
          </div>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      {log.isPending && <p className="muted">Caricamento…</p>}
      {log.data?.length === 0 && <p className="muted">Ancora nessuna nota.</p>}
      {log.data && log.data.length > 0 && (
        <ul className="plain-list offer-log">
          {log.data.map((entry) => (
            <li key={entry.id}>
              <div className="row between">
                <span>
                  <strong>{OFFER_LOG_LABELS[entry.kind]}</strong>
                  {entry.contact && ` con ${entry.contact}`}
                  <span className="muted small">
                    {' '}
                    · {formatTimestamp(entry.occurredAt)}
                    {entry.author && ` · ${entry.author.name}`}
                  </span>
                </span>
                {entry.editable && (
                  <span className="row">
                    <button
                      type="button"
                      className="link small"
                      onClick={() => {
                        setEditing(entry.id);
                        setDraft({
                          kind: entry.kind,
                          contact: entry.contact ?? '',
                          text: entry.text,
                          at: localInput(new Date(entry.occurredAt)),
                        });
                      }}
                    >
                      Correggi
                    </button>
                    <button
                      type="button"
                      className="link small danger"
                      onClick={() => remove.mutate(entry.id)}
                    >
                      Elimina
                    </button>
                  </span>
                )}
              </div>
              <p className="pre-line">{entry.text}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
